import { inject, Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { BehaviorSubject, catchError, filter, finalize, Observable, take, throwError } from 'rxjs';
import { AuthService } from '../services';
import { switchMap } from 'rxjs/operators';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private readonly authService = inject(AuthService);

  private refreshTokenSubject: BehaviorSubject<boolean> | null = null;

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    req = req.clone({ withCredentials: true });

    if (this.isAuthEndpoint(req.url)) {
      return next.handle(req);
    }

    return next.handle(req).pipe(
      catchError((error) => {
        if (error instanceof HttpErrorResponse && error.status === 401) {
          return this.handle401Error(req, next);
        }
        return throwError(() => error);
      }),
    );
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.refreshTokenSubject !== null) {
      return this.refreshTokenSubject.pipe(
        filter(Boolean),
        take(1),
        switchMap(() => next.handle(request)),
      );
    }
    this.refreshTokenSubject = new BehaviorSubject<boolean>(false);

    return this.authService.refreshToken().pipe(
      switchMap(() => {
        this.refreshTokenSubject.next(true);
        return next.handle(request);
      }),
      catchError((err) => {
        this.authService.logOutOnExpiredRefreshToken();
        return throwError(() => err);
      }),
      finalize(() => {
        this.refreshTokenSubject = null;
      }),
    );
  }

  private isAuthEndpoint(url: string): boolean {
    return (
      url.includes('/auth/login') || url.includes('/auth/refresh') || url.includes('/auth/logout')
    );
  }
}
